import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text, Link } from 'rebass/styled-components';
import { ProfilePicture, Tooltip } from '@getro/rombo';
import { ChevronDown, Mail } from 'lucide-react';
import { TableRow } from '../../../components/molecules/agGrid/components/tableRow';

export const IntrosRenderer = ({ data, ...props }) => {
  if (!data.original) {
    return '';
  }

  const names = data.introOffers.map((intro) => intro.offererName).join(', ');
  const visibleIntros = data.introOffers.slice(0, 3);
  const introCount = Math.max(0, data.introOffers.length - visibleIntros.length);
  const gridContext = props.api?.getGridOption?.('context') || {};
  const rowOptions = gridContext[data.rowId] ?? {};

  const toggleIntros = () => {
    const context = props.api?.getGridOption?.('context') || {};
    const rowContext = context[data.rowId] ?? {};

    rowContext.showAll = !rowContext?.showAll;
    rowContext.height = rowContext?.showAll ? data.introOffers.length * 30 + 16 : 40;

    props.node.setRowHeight(rowContext.height);

    props.api.setGridOption('context', {
      ...(props.api?.getGridOption?.('context') || {}),
      [data.rowId]: rowContext,
    });

    props.api.onRowHeightChanged();
    props.api.redrawRows({
      rowNodes: [props.node],
    });
  };

  if (data.original.id === 'load-more' || data.original.noContacts) {
    return '';
  }

  if (data.introOffers.length === 0) {
    return (
      <Text padding="8px" fontSize="14px" fontStyle="italic" color="text.subtle">
        No intros offered yet
      </Text>
    );
  }

  return (
    <TableRow cellStyle={{ padding: '8px' }} {...props}>
      <Flex sx={{ padding: '8px', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
        {!rowOptions?.showAll && (
          <Flex width="100%" alignItems="center" sx={{ gap: '8px' }}>
            <Flex sx={{ flexShrink: 0 }}>
              {visibleIntros.map((intro, index) => (
                <Box
                  key={`intro-avatar-${intro.id || intro.offererName}`}
                  width="24px"
                  height="24px"
                  p="2px"
                  ml={index === 0 ? '0' : '-10px'}
                  flex="0 0 auto"
                  sx={{ bg: 'neutral.0', borderRadius: 'circle', zIndex: 1 }}
                >
                  <ProfilePicture name={intro.offererName} imageUrl={intro.offererAvatarUrl} />
                </Box>
              ))}
              {introCount > 0 && (
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  bg="neutral.100"
                  width="24px"
                  height="24px"
                  ml="-14px"
                  flex="0 0 auto"
                  sx={{
                    borderRadius: 'circle',
                    zIndex: 1,
                    borderWidth: '2px',
                    borderColor: 'neutral.0',
                    borderStyle: 'solid',
                  }}
                >
                  <Text fontSize="0" fontWeight="medium">
                    {`+${introCount}`}
                  </Text>
                </Flex>
              )}
            </Flex>
            <Box>
              <Text
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '14px',
                  color: 'text.main',
                }}
              >
                {names}
              </Text>
            </Box>
          </Flex>
        )}
        {rowOptions?.showAll && (
          <Flex flexDirection="column" sx={{ gap: '4px' }}>
            {data.introOffers.map((intro) => (
              <Flex
                key={`intro-${intro.id || intro.offererName}`}
                alignItems="center"
                sx={{
                  gap: '8px',
                  '&:hover .table-item-intros-actions': {
                    visibility: 'visible',
                  },
                }}
              >
                <Flex alignItems="center" flexGrow={1} sx={{ gap: '8px' }}>
                  <Box width="24px" height="24px" flex="0 0 auto">
                    <ProfilePicture imageUrl={intro.offererAvatarUrl} name={intro.offererName} />
                  </Box>
                  <Text
                    fontSize="14px"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: 'text.subtle',
                    }}
                  >
                    {`${intro.offererName}${intro.offererCollection ? ` ${intro.offererCollection}` : ''}`}
                  </Text>
                </Flex>
                <Box className="table-item-intros-actions" pt="4px" flex="0 0 auto" sx={{ visibility: 'hidden' }}>
                  <Tooltip
                    trigger={
                      <Link href={`mailto:${intro.offererEmail}`} sx={{ color: 'text.subtle' }}>
                        <Box as={Mail} height="16px" width="16px" aria-hidden="true" sx={{ cursor: 'pointer' }} />
                      </Link>
                    }
                    content="Compose and send forwardable"
                  />
                </Box>
              </Flex>
            ))}
          </Flex>
        )}
        {data.introOffers.length > 1 && (
          <Box
            sx={{
              cursor: 'pointer',
              transform: rowOptions?.showAll ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s ease-in-out',
              color: 'neutral.500',
            }}
            onClick={() => toggleIntros()}
          >
            <ChevronDown size={16} />
          </Box>
        )}
      </Flex>
    </TableRow>
  );
};
IntrosRenderer.propTypes = {
  data: PropTypes.object,
  context: PropTypes.object,
  api: PropTypes.object,
  node: PropTypes.object,
};

IntrosRenderer.defaultProps = {
  data: {},
  context: {},
  api: {},
  node: {},
};
